// src/pages/MyListings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getListingsByUserId, archiveListing, restoreListing } from '../services/ListingsService';
import './Listings.css';

// Placeholder image for listings without images (local asset to avoid network issues)
const placeholderImage = '/placeholder.jpg';

function MyListings() {
  const { currentUser } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active'); // active | archived | all

  // Fetch listings owned by the current user
  useEffect(() => {
    const fetchMyListings = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const myListings = await getListingsByUserId(currentUser.uid);
        setListings(myListings);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching your listings:", err);
        setError("Failed to load your listings. Please try again later.");
        setLoading(false);
      }
    };

    fetchMyListings();
  }, [currentUser]);

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleArchiveToggle = async (id, shouldArchive) => {
    if (!window.confirm(`Are you sure you want to ${shouldArchive ? 'archive' : 'restore'} this listing?\n\nClick OK to ${shouldArchive ? 'archive' : 'restore'}.`)) {
      return;
    }
    
    try {
      if (shouldArchive) {
        await archiveListing(id);
      } else {
        await restoreListing(id);
      }
      setListings(prev =>
        prev.map(listing =>
          listing.id === id
            ? {
                ...listing,
                archived: shouldArchive,
                archivedAt: shouldArchive ? new Date() : null
              }
            : listing
        )
      );
    } catch (err) {
      console.error("Error updating listing status:", err);
      alert("Failed to update listing status. Please try again.");
    }
  };

  const filteredListings = listings.filter(listing => {
    if (filter === 'all') return true;
    if (filter === 'archived') return listing.archived === true;
    return listing.archived !== true;
  });

  if (loading) {
    return <div className="listings-container">Loading your listings...</div>;
  }

  if (error) {
    return <div className="listings-container">{error}</div>;
  }

  return (
    <div className="listings-container">
      <div className="listings-header" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div>
          <h1>My Listings</h1>
          <p>Manage your posted listings.</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', border: '1px solid #e0e0e0', borderRadius: '999px', overflow: 'hidden' }}>
            {['active', 'archived', 'all'].map(option => (
              <button
                key={option}
                type="button"
                onClick={() => setFilter(option)}
                style={{
                  padding: '0.35rem 1rem',
                  backgroundColor: filter === option ? 'var(--color-primary)' : 'transparent',
                  color: filter === option ? '#fff' : '#555',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  textTransform: 'capitalize'
                }}
              >
                {option}
              </button>
            ))}
          </div>
          <Link 
            to="/add-listing" 
            style={{
              display: 'inline-block',
              backgroundColor: 'var(--color-primary)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              textDecoration: 'none'
            }}
          >
            + Add New Listing
          </Link>
        </div>
      </div>

      {filteredListings.length === 0 ? (
        <div className="empty-listings">
          <p>
            {filter === 'archived'
              ? "You don't have any archived listings yet."
              : filter === 'active'
                ? "You don't have any active listings at the moment."
                : "You haven't created any listings yet. Click the button above to add your first listing."}
          </p>
        </div>
      ) : (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {filteredListings.map(listing => (
            <div 
              key={listing.id} 
              style={{
                display: 'flex',
                marginBottom: '2rem',
                border: '1px solid #eee',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: 'white',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ width: '120px', height: '120px', flexShrink: 0 }}>
                <img 
                  src={listing.imageUrl || placeholderImage} 
                  alt={listing.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              
              <div style={{ flex: 1, padding: '1rem' }}>
                <h3 style={{ margin: '0 0 0.5rem', color: 'var(--color-primary)' }}>
                  {listing.title}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <span style={{ color: listing.archived ? '#c0392b' : '#2ecc71', fontWeight: 600 }}>
                    {listing.archived ? 'Archived' : 'Active'}
                  </span>
                  {listing.category && (
                    <span style={{ color: '#777' }}>
                      • {listing.category.charAt(0).toUpperCase() + listing.category.slice(1)}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <span>Posted: {formatDate(listing.createdAt)}</span>
                  {listing.archived && listing.archivedAt && (
                    <span>Archived: {formatDate(listing.archivedAt)}</span>
                  )}
                </div>
                {listing.isVirtual ? (
                  <div style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.75rem' }}>Virtual listing</div>
                ) : (
                  <div style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.75rem' }}>
                    {listing.locations?.length > 1 ? (
                      <>
                        <strong>{listing.locations.length}</strong> locations
                        <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, listStyle: 'disc' }}>
                          {listing.locations.slice(0, 2).map((loc, idx) => (
                            <li key={loc.id || idx} style={{ marginBottom: '0.15rem' }}>
                              {loc.address}
                              {idx === 1 && listing.locations.length > 2 && '…'}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <span>{listing.location || 'Location pending'}</span>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  <Link 
                    to={`/listings/${listing.id}`}
                    style={{
                      backgroundColor: 'var(--color-primary-rgb-15)',
                      color: 'var(--color-primary)',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontSize: '0.9rem'
                    }}
                  >
                    View
                  </Link>
                  <Link 
                    to={`/edit-listing/${listing.id}`}
                    style={{
                      backgroundColor: '#4a90e2',
                      color: 'white',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontSize: '0.9rem'
                    }}
                  >
                    Edit
                  </Link>
                  <button 
                    onClick={() => handleArchiveToggle(listing.id, listing.archived !== true)}
                    style={{
                      backgroundColor: listing.archived ? '#27ae60' : '#f39c12',
                      color: 'white',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {listing.archived ? 'Restore' : 'Archive'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyListings;
